import { useCallback, useMemo } from "react"

import lodash from "lodash"
import { useParams } from "react-router-dom"

import {
    Layer,
    MapStyles,
    ROADWORK_CLICKABLE_LAYER,
    ROADWORK_LAYER,
    ROADWORK_SOURCE,
    SELECTED_LINE_LAYER_SIZE,
    getInterpolatedLineLayerWidth,
    getInterpolatedSelectableLineLayerWidth,
    useMapDispatchContext,
    useMapStateContext,
    useOrderingLayers,
} from "@l2r-front/l2r-map"
import { useNetworksStateContext } from "@l2r-front/l2r-networks"
import { useTheme } from "@l2r-front/l2r-ui"
import { useNavigateWithSearchParams } from "@l2r-front/l2r-utils"

import { ROADWORK_LAYER_WIDTH, getRoadworksLayerStyle } from "../../constants/getRoadworkConfig"
import { useRoadworksDispatchContext, useRoadworksStateContext } from "../../contexts/RoadworksContext"

import { RoadworksSource } from "../RoadworksSource"

export function RoadworksLayer() {

    const theme = useTheme()
    const { getCurrentMapStyle } = useMapStateContext()
    const { selectedLinearLocations } = useNetworksStateContext()
    const { filters, selectedRoadwork } = useRoadworksStateContext()
    const { road, roadworkUuid } = useParams()
    const navigate = useNavigateWithSearchParams()
    const selectedRoad = selectedLinearLocations?.[0]?.road
    const { setMapPin } = useMapDispatchContext()
    const { setSelectedSegment } = useRoadworksDispatchContext()

    const roadworksLayerConfig = useMemo(() => {
        const referentialColor = getCurrentMapStyle() === MapStyles.plan ? theme.palette.map.darkReferential
            : theme.palette.map.lightReferential

        return lodash.merge(
            getRoadworksLayerStyle(referentialColor, selectedRoadwork, selectedRoad, filters, theme, roadworkUuid),
            {
                "paint": {
                    "line-width": getInterpolatedSelectableLineLayerWidth(selectedRoad, ROADWORK_LAYER_WIDTH),
                },
            },
        )
    }, [getCurrentMapStyle, filters, roadworkUuid, selectedRoad, selectedRoadwork, theme])

    const roadworksLayerClickConfig = useMemo(() => {
        return lodash.merge(getRoadworksLayerStyle("transparent", selectedRoadwork, selectedRoad, filters, theme),
            {
                "paint": {
                    "line-color": "transparent",
                    "line-width": getInterpolatedLineLayerWidth(SELECTED_LINE_LAYER_SIZE),
                },
            })
    }, [filters, selectedRoad, selectedRoadwork, theme])

    const handleLayerClick = useCallback((event) => {
        setMapPin()
        setSelectedSegment(event.feature)
        const linearLocation = JSON.parse(event.feature.properties.linearLocation)[0]
        navigate((roadworkUuid || road) ? `../${linearLocation.road}/${event.feature.properties.uuid}`
            : `${linearLocation.road}/${event.feature.properties.uuid}`)
    }, [navigate, road, roadworkUuid, setMapPin, setSelectedSegment])

    useOrderingLayers([ROADWORK_CLICKABLE_LAYER, ROADWORK_LAYER])

    return <RoadworksSource id={ROADWORK_SOURCE}>
        <Layer
            {...roadworksLayerConfig}
            id={ROADWORK_LAYER}
        />
        <Layer
            {...roadworksLayerClickConfig}
            id={ROADWORK_CLICKABLE_LAYER}
            onClick={handleLayerClick}
        />
    </RoadworksSource>
}